import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setName(user.name || '');
    }
  }, [user]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState({ email: false, name: false, password: false });

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    setLoading({ ...loading, email: true });
    try {
      const response = await axios.put(
        `${API_URL}/auth/update-email`,
        { email },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setEmailSuccess('Email updated successfully!');
      if (refreshUser) {
        await refreshUser();
      }
      // Email will be updated via useEffect when user state changes
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Failed to update email');
    } finally {
      setLoading({ ...loading, email: false });
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setNameError('');
    setNameSuccess('');
    
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }

    setLoading({ ...loading, name: true });
    try {
      const response = await axios.put(
        `${API_URL}/auth/update-name`,
        { name },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setNameSuccess('Name updated successfully!');
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      setNameError(err.response?.data?.error || 'Failed to update name');
    } finally {
      setLoading({ ...loading, name: false });
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setLoading({ ...loading, password: true });
    try {
      const response = await axios.put(
        `${API_URL}/auth/update-password`,
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading({ ...loading, password: false });
    }
  };

  return (
    <div className="container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings</p>
      </div>

      <div className="settings-content">
        {/* Update Email Section */}
        <div className="settings-section">
          <h2>Update Email Address</h2>
          <form onSubmit={handleUpdateEmail} className="settings-form">
            <div className="form-group">
              <label htmlFor="email">New Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={user?.email || 'Enter new email'}
                className="form-input"
                disabled={loading.email}
              />
            </div>
            {emailError && <div className="error-message">{emailError}</div>}
            {emailSuccess && <div className="success-message">{emailSuccess}</div>}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading.email}
            >
              {loading.email ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>

        {/* Update Name Section */}
        <div className="settings-section">
          <h2>Update Display Name</h2>
          <form onSubmit={handleUpdateName} className="settings-form">
            <div className="form-group">
              <label htmlFor="name">Display Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={user?.name || 'Enter your name'}
                className="form-input"
                disabled={loading.name}
              />
            </div>
            {nameError && <div className="error-message">{nameError}</div>}
            {nameSuccess && <div className="success-message">{nameSuccess}</div>}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading.name}
            >
              {loading.name ? 'Updating...' : 'Update Name'}
            </button>
          </form>
        </div>

        {/* Update Password Section */}
        <div className="settings-section">
          <h2>Change Password</h2>
          <form onSubmit={handleUpdatePassword} className="settings-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="form-input"
                disabled={loading.password}
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="form-input"
                disabled={loading.password}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="form-input"
                disabled={loading.password}
              />
            </div>
            {passwordError && <div className="error-message">{passwordError}</div>}
            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading.password}
            >
              {loading.password ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Delete Account Section */}
        <div className="settings-section delete-account-section">
          <h2>Delete Account</h2>
          <p className="delete-account-description">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button 
            className="btn btn-danger"
            onClick={() => navigate('/delete-account')}
          >
            Delete My Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
